import { getSupabaseClient } from './supabase';
import { getLandingEditorApiOrigin, isLandingEditorApiConfigured } from './landing-editor-api';

/** A site the authenticated user can access (own or agency), from GET /api/sites. */
export interface SiteListItem {
  id: string;
  name: string;
  subdomain: string | null;
  isPublished: boolean;
  publishedUrl: string | null;
  thumbnailUrl: string | null;
  agencyId: string | null;
  updatedAt: string | null;
  createdAt: string | null;
}

interface SitesResponse {
  sites?: SiteListItem[];
  error?: string;
}

async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const supabase = getSupabaseClient();
  if (!supabase) {
    return headers;
  }
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/**
 * List every site the authenticated user can access (their own + agency sites).
 * RBAC is enforced server-side (Supabase RLS + explicit filter). Returns [] when
 * the user has no sites; throws on network/permission failures.
 */
export async function listSites(): Promise<SiteListItem[]> {
  if (!isLandingEditorApiConfigured()) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    throw new Error('Sign in to view your sites.');
  }

  const response = await fetch(`${origin}/api/sites`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  const body = (await response.json().catch(() => ({}))) as SitesResponse;
  if (!response.ok) {
    throw new Error(body.error ?? `Failed to load sites (${response.status})`);
  }
  return body.sites ?? [];
}
